import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { useAuth } from "../../../providers/AuthProvider";
import { LotMapSection } from "../components/LotMapSection";
import { getWorkspace, type Workspace } from "../../workspaces/api/workspaces";
import { listLots, type Lot } from "../api/lots";

import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { SectionWithHeader } from "../../shared/ui/SectionWithHeader";
import { MetricTile } from "../../shared/ui/MetricTile";
import { useResponsiveBreakpoint } from "../../../hooks/useResponsiveBreakpoint";
import {
  ArrowLeft,
  Plus,
  SquaresFourIcon,
} from "@phosphor-icons/react";
import { iconLibrary, summaryIconProps } from "../../shared/ui/icons";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("WorkspaceLotsMapPage");

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const actionsStackClasses = "flex flex-wrap items-center gap-2";
const secondaryButtonClasses = "pm-btn pm-btn-secondary";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const ghostButtonClasses = "pm-btn pm-btn-ghost pm-btn-sm";
const errorBannerClasses =
  "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const summaryGridClasses = "grid gap-4 md:grid-cols-2 xl:grid-cols-4";
const lotGridClasses = "grid gap-4 md:grid-cols-2 xl:grid-cols-3";
const lotCardClasses =
  "flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-xl transition hover:-translate-y-0.5 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700";
const lotTitleClasses = "text-xl font-semibold text-slate-900 dark:text-slate-100";
const lotSubtitleClasses = "text-sm text-slate-500 dark:text-slate-400";
const lotMetricsClasses =
  "grid grid-cols-1 gap-3 text-sm text-slate-600 sm:grid-cols-2 dark:text-slate-400";
const lotMetricLabelClasses =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400";
const lotSkeletonClasses =
  "flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm animate-pulse dark:border-slate-800 dark:bg-slate-900";
const lotSkeletonLine = "h-4 rounded bg-slate-200 dark:bg-slate-800";

export function WorkspaceLotsMapPage() {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useResponsiveBreakpoint();

  const [workspaceDetails, setWorkspaceDetails] = useState<Workspace | undefined>();
  const [lots, setLots] = useState<Lot[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initialWorkspace = (location.state as { workspace?: Workspace } | undefined)?.workspace;
  const workspaceLinkState = useMemo(
    () => (workspaceDetails ? { workspace: workspaceDetails } : undefined),
    [workspaceDetails],
  );

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    return [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      {
        label: workspaceDetails?.name ?? t("workspaces.lots.page_title_fallback"),
        to: workspaceId ? `/workspaces/${workspaceId}` : undefined,
        state: workspaceLinkState,
      },
      { label: t("workspaces.lots.map.breadcrumb") },
    ];
  }, [t, workspaceDetails?.name, workspaceId, workspaceLinkState]);

  const handleBack =
    useBreadcrumbBack({
      items: breadcrumbItems,
      includeHome: false,
      fallback: { to: "/workspaces" },
    }) ?? (() => navigate(-1));

  const workspaceNameMap = useMemo(() => {
    if (!workspaceDetails?.id || !workspaceDetails?.name) return {};
    return { [workspaceDetails.id]: workspaceDetails.name };
  }, [workspaceDetails]);

  const loadWorkspaceDetails = useCallback(async () => {
    if (!accessToken || !workspaceId) return;
    try {
      const details = await getWorkspace(accessToken, workspaceId);
      setWorkspaceDetails(details);
    } catch (err) {
      logger.warn("Failed to load workspace", err);
    }
  }, [accessToken, workspaceId]);

  const loadLots = useCallback(async () => {
    if (!accessToken || !workspaceId) return;
    setIsLoading(true);
    setError(null);
    try {
      const fetchedLots = await listLots(accessToken, { workspace_id: workspaceId, limit: 500 });
      const activeLots =
        Array.isArray(fetchedLots) && fetchedLots.length > 0
          ? fetchedLots.filter((lot) => !lot.archived_at)
          : [];
      setLots(activeLots);
    } catch (err) {
      logger.error(err);
      setError(t("workspaces.lots.map.load_error"));
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, t, workspaceId]);

  useEffect(() => {
    if (initialWorkspace) {
      setWorkspaceDetails(initialWorkspace);
    }
    void loadWorkspaceDetails();
  }, [initialWorkspace, loadWorkspaceDetails]);

  useEffect(() => {
    void loadLots();
  }, [loadLots]);

  const snapshotFileBase = useMemo(() => {
    const name = workspaceDetails?.name?.trim();
    const base = name ?? workspaceId ?? "workspace";
    const slug = base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    return slug ? `${slug}-lots-map` : "workspace-lots-map";
  }, [workspaceDetails?.name, workspaceId]);

  const totalArea = useMemo(() => {
    return lots.reduce((sum, lot) => sum + (lot.computed_area_m2 ?? 0), 0);
  }, [lots]);

  const totalTraverse = useMemo(() => {
    return lots.reduce((sum, lot) => sum + (lot.traverse_length_m ?? 0), 0);
  }, [lots]);

  const averageClosureRatio = useMemo(() => {
    const ratios = lots
      .map((lot) => lot.closure_ratio)
      .filter((ratio): ratio is number => Number.isFinite(ratio ?? NaN));
    if (ratios.length === 0) return null;
    const average = ratios.reduce((sum, ratio) => sum + ratio, 0) / ratios.length;
    return average;
  }, [lots]);

  const headers = useMemo(
    () => [
      {
        label: t("workspaces.lots.map.summary.active"),
        value: lots.length.toLocaleString(),
        icon: iconLibrary.lotsActive,
      },
      {
        label: t("workspaces.lots.map.summary.area"),
        value:
          totalArea > 0
            ? `${totalArea.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} m²`
            : "—",
        icon: iconLibrary.lotsArea,
      },
      {
        label: t("workspaces.lots.map.summary.traverse"),
        value:
          totalTraverse > 0
            ? `${totalTraverse.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })} m`
            : "—",
        icon: iconLibrary.lotsTraverse,
      },
      {
        label: t("workspaces.lots.map.summary.closure"),
        value:
          averageClosureRatio && averageClosureRatio > 0
            ? `1:${averageClosureRatio.toLocaleString(undefined, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}`
            : "—",
        icon: iconLibrary.lotsClosure,
      },
    ],
    [averageClosureRatio, lots.length, t, totalArea, totalTraverse],
  );

  const showMetricSkeletons = isLoading && lots.length === 0;

  return (
    <PageShell width="wide" className={pageStackClasses} showFooter={!isMobile}>
      <Breadcrumbs items={breadcrumbItems} />
      <PageHeader
        title={
          workspaceDetails?.name
            ? t("workspaces.lots.map.title_with_name", {
                name: workspaceDetails.name,
              })
            : t("workspaces.lots.map.title_loading")
        }
        subtitle={
          workspaceDetails?.name
            ? t("workspaces.lots.map.subtitle")
            : t("workspaces.lots.map.subtitle_loading")
        }
        actions={
          <div className={actionsStackClasses}>
            {workspaceId ? (
              <Link className={secondaryButtonClasses} to={`/workspaces/${workspaceId}/lots`}>
                {t("workspaces.lots.map.manage_button")}
              </Link>
            ) : null}
            {workspaceId ? (
              <Link
                className={primaryButtonClasses}
                to={`/workspaces/${workspaceId}/lots/new`}
                state={workspaceLinkState}
              >
                <Plus className="h-5 w-5" aria-hidden />
                {t("workspaces.lots.map.add_button")}
              </Link>
            ) : null}
            <button
              type="button"
              className={[ghostButtonClasses, "ml-auto"].join(" ")}
              onClick={handleBack}
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
              {t("actions.back")}
            </button>
          </div>
        }
      />

      {error ? <div className={errorBannerClasses}>{error}</div> : null}

      <LotMapSection
        accessToken={accessToken}
        workspaceIds={workspaceId ? [workspaceId] : undefined}
        workspaceNames={workspaceNameMap}
        snapshotFileBase={snapshotFileBase}
        mapIdPrefix={workspaceId ? `workspace-${workspaceId}-map` : "workspace-map"}
        onLotNavigate={(wsId, lotId) =>
          navigate(`/workspaces/${wsId}/lots/${lotId}`, { state: workspaceLinkState })
        }
      />

      <section className={summaryGridClasses}>
        {showMetricSkeletons
          ? Array.from({ length: 4 }).map((_, idx) => (
              <div
                key={`metric-skel-${idx}`}
                className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800" />
                <div className="h-6 w-32 rounded bg-slate-300 dark:bg-slate-700" />
              </div>
            ))
          : headers.map((header) => {
              const Icon = header.icon ?? SquaresFourIcon;
              return (
                <MetricTile
                  key={header.label}
                  icon={<Icon {...summaryIconProps} aria-hidden />}
                  label={header.label}
                  value={header.value}
                />
              );
            })}
      </section>

      <SectionWithHeader
        title={t("workspaces.lots.map.section_heading")}
        description={t("workspaces.lots.map.section_description")}
      >
        {!isLoading && lots.length > 0 ? (
          <div className={lotGridClasses}>
            {lots.map((lot) => (
              <Link
                key={lot.id}
                to={`/workspaces/${workspaceId}/lots/${lot.id}`}
                state={workspaceLinkState}
                className={lotCardClasses}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex flex-col gap-1">
                    <h3 className={lotTitleClasses}>{lot.name || t("workspaces.lots.untitled")}</h3>
                    <p className={lotSubtitleClasses}>
                      {lot.title_no
                        ? t("workspaces.lots.map.card_title_number", {
                            number: lot.title_no,
                          })
                        : t("workspaces.lots.map.card_no_title")}
                    </p>
                  </div>
                </div>
                <div className={lotMetricsClasses}>
                  <div>
                    <div className={lotMetricLabelClasses}>{t("workspaces.lots.map.summary.area")}</div>
                    <div>
                      {lot.computed_area_m2
                        ? `${lot.computed_area_m2.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} m²`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className={lotMetricLabelClasses}>{t("workspaces.lots.map.summary.traverse")}</div>
                    <div>
                      {lot.traverse_length_m
                        ? `${lot.traverse_length_m.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} m`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className={lotMetricLabelClasses}>{t("workspaces.lots.map.summary.closure")}</div>
                    <div>
                      {lot.closure_ratio
                        ? `1:${lot.closure_ratio.toLocaleString(undefined, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          })}`
                        : "—"}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : isLoading ? (
          <div className={lotGridClasses}>
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={`lot-skel-${idx}`} className={lotSkeletonClasses}>
                <div className={`${lotSkeletonLine} w-1/3`} />
                <div className={`${lotSkeletonLine} w-2/5`} />
                <div className="grid grid-cols-2 gap-2">
                  <div className={lotSkeletonLine} />
                  <div className={lotSkeletonLine} />
                  <div className={`${lotSkeletonLine} col-span-2`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-600 dark:border-slate-800 dark:text-slate-400">
            {t("workspaces.lots.map.empty_state")}
          </div>
        )}
      </SectionWithHeader>
    </PageShell>
  );
}
